<template>
  <div class="absolute inset-0 overflow-hidden pointer-events-none">
    <!-- 浮动几何图形 -->
    <div 
      v-for="(shape, index) in shapes" 
      :key="index"
      :class="[
        'absolute rounded-full blur-3xl',
        shape.class,
        shape.animation
      ]"
      :style="shape.style"
    ></div>
    
    <!-- 粒子效果容器 -->
    <div v-if="showParticles" class="particles-container">
      <div 
        v-for="i in particleCount" 
        :key="i"
        :class="[
          'particle',
          `particle-${i}`
        ]"
      ></div>
    </div>
    
    <!-- 流光效果 -->
    <div 
      v-if="showStreamingLight"
      class="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/30 to-transparent streaming-light"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  variant?: 'hero' | 'product' | 'minimal';
  showParticles?: boolean;
  showStreamingLight?: boolean;
  particleCount?: number;
  customShapes?: Array<{
    class: string;
    style: string;
    animation: string;
  }>;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'hero',
  showParticles: true,
  showStreamingLight: true,
  particleCount: 6,
  customShapes: undefined
});

const shapes = computed(() => {
  if (props.customShapes) {
    return props.customShapes;
  }

  switch (props.variant) {
    case 'hero':
      return [
        {
          class: 'w-64 h-64 bg-gradient-to-br from-primary/10 to-primary/5',
          style: 'top: 5rem; left: 2.5rem;',
          animation: 'animate-float-slow'
        },
        {
          class: 'w-48 h-48 bg-gradient-to-bl from-primary/15 to-transparent',
          style: 'top: 10rem; right: 5rem;',
          animation: 'animate-float-reverse'
        },
        {
          class: 'w-32 h-32 bg-gradient-to-tr from-primary/20 to-primary/8',
          style: 'bottom: 5rem; left: 25%;',
          animation: 'animate-float-fast'
        }
      ];
    case 'product':
      return [
        {
          class: 'w-72 h-72 bg-gradient-to-br from-primary/8 to-primary/3',
          style: 'top: 2rem; right: 2rem;',
          animation: 'animate-float-gentle'
        },
        {
          class: 'w-56 h-56 bg-gradient-to-bl from-blue-500/10 to-transparent',
          style: 'bottom: 3rem; left: 3rem;',
          animation: 'animate-float-reverse'
        }
      ];
    case 'minimal':
      return [
        {
          class: 'w-40 h-40 bg-gradient-to-br from-primary/5 to-transparent',
          style: 'top: 10%; right: 10%;',
          animation: 'animate-pulse-slow'
        }
      ];
    default:
      return [];
  }
});
</script>

<style scoped>
/* 浮动动画 */
@keyframes floatSlow {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  25% {
    transform: translateY(-20px) translateX(10px);
  }
  50% {
    transform: translateY(-10px) translateX(-5px);
  }
  75% {
    transform: translateY(-30px) translateX(15px);
  }
}

@keyframes floatReverse {
  0%, 100% {
    transform: translateY(0px) translateX(0px) scale(1);
  }
  33% {
    transform: translateY(15px) translateX(-10px) scale(1.1);
  }
  66% {
    transform: translateY(-25px) translateX(8px) scale(0.9);
  }
}

@keyframes floatFast {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-40px) rotate(180deg);
  }
}

@keyframes floatGentle {
  0%, 100% {
    transform: translateY(0px) scale(1);
    opacity: 0.8;
  }
  50% {
    transform: translateY(-8px) scale(1.1);
    opacity: 1;
  }
}

@keyframes pulseSlow {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
}

@keyframes streamingLight {
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100vw);
    opacity: 0;
  }
}

/* 粒子动画 */
@keyframes particle1 {
  0% {
    transform: translateY(100vh) translateX(0px) scale(0);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) translateX(50px) scale(1);
    opacity: 0;
  }
}

@keyframes particle2 {
  0% {
    transform: translateY(100vh) translateX(0px) scale(0);
    opacity: 0;
  }
  15% {
    opacity: 1;
  }
  85% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100px) translateX(-30px) scale(1);
    opacity: 0;
  }
}

/* 应用动画类 */
.animate-float-slow {
  animation: floatSlow 8s ease-in-out infinite;
}

.animate-float-reverse {
  animation: floatReverse 6s ease-in-out infinite;
  animation-delay: -2s;
}

.animate-float-fast {
  animation: floatFast 4s ease-in-out infinite;
  animation-delay: -1s;
}

.animate-float-gentle {
  animation: floatGentle 3s ease-in-out infinite;
}

.animate-pulse-slow {
  animation: pulseSlow 4s ease-in-out infinite;
}

.streaming-light {
  animation: streamingLight 8s linear infinite;
}

/* 粒子容器 */
.particles-container {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.particle {
  position: absolute;
  width: 4px;
  height: 4px;
  background: hsl(var(--primary) / 0.6);
  border-radius: 50%;
}

.particle-1 {
  left: 10%;
  animation: particle1 15s linear infinite;
}

.particle-2 {
  left: 20%;
  animation: particle2 12s linear infinite;
  animation-delay: -3s;
}

.particle-3 {
  left: 30%;
  animation: particle1 18s linear infinite;
  animation-delay: -6s;
}

.particle-4 {
  left: 60%;
  animation: particle2 14s linear infinite;
  animation-delay: -9s;
}

.particle-5 {
  left: 80%;
  animation: particle1 16s linear infinite;
  animation-delay: -12s;
}

.particle-6 {
  left: 90%;
  animation: particle2 13s linear infinite;
  animation-delay: -15s;
}

/* 响应式优化 */
@media (max-width: 768px) {
  .animate-float-slow,
  .animate-float-reverse,
  .animate-float-fast {
    animation-duration: 6s;
  }
  
  .particles-container {
    display: none; /* 移动端隐藏粒子效果以提升性能 */
  }
}
</style>
